import { Injectable } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { isDateValidator } from '../helpers/inputs';
import { InfoForm } from '../helpers/models/info-form.model';
import { ApiService } from '../services/api.service';
import { VippsStorage } from './vipps-stored.service';

@Injectable({
  providedIn: 'root',
})
export class FormStorage {
  private _educationForm: BehaviorSubject<FormGroup> = new BehaviorSubject(
    undefined
  );
  private _interestsAndDreamsForm: BehaviorSubject<FormGroup> =
    new BehaviorSubject(undefined);

  private _affilatesForm: BehaviorSubject<FormGroup> = new BehaviorSubject(
    undefined
  );

  private _coursesForm: BehaviorSubject<FormGroup> = new BehaviorSubject(
    undefined
  );

  private _infoForm: BehaviorSubject<FormGroup> = new BehaviorSubject(
    undefined
  );

  get interestsAndDreamsForm(): Observable<FormGroup> {
    return this._interestsAndDreamsForm.asObservable();
  }

  get educationForm(): Observable<FormGroup> {
    return this._educationForm.asObservable();
  }

  get coursesForm(): Observable<FormGroup> {
    return this._coursesForm.asObservable();
  }

  get affilatesForm(): Observable<FormGroup> {
    return this._affilatesForm.asObservable();
  }

  get infoForm(): Observable<FormGroup> {
    return this._infoForm.asObservable();
  }

  get infoFormV(): FormGroup {
    return this._infoForm.value;
  }

  get coursesFormV(): FormGroup {
    return this._coursesForm.value;
  }

  get educationFormV(): FormGroup {
    return this._educationForm.value;
  }

  get interestFormV(): FormGroup {
    return this._interestsAndDreamsForm.value;
  }

  get affilatesFormV(): FormGroup {
    return this._affilatesForm.value;
  }
  private _isUserAuthorizedThroughVipps: boolean = false

  get isUserAuthorizedThroughVipps(): boolean {
    return this._isUserAuthorizedThroughVipps;
  }
  constructor(
    private _fb: FormBuilder,
    private apiService: ApiService,
    private vippsInfo: VippsStorage
  ) {
    const education = [
      { index: 0, toggled: false, name: 'Har fullført grunnskolen' },
      {
        index: 1,
        toggled: false,
        name: 'Videregående skole',
        questionsAndAnswers: [
          {
            question: 'Videregående første år.',
            id: 'highschoolfirst',
            answers: [
              'Design og Håndverk/ Formgivingsfag o.l',
              'Andre yrkesfaglige linjer ',
              'Studiespesialisering / Allmennfag o.l',
              'Ingen av alternativene over passer',
            ],
          },
          {
            question: 'Videregående andre år.',
            id: 'highschoolsecond',
            answers: ['Ja', 'Nei'],
          },
          {
            question: 'Videregående tredje år.',
            id: 'highschoolthird',
            answers: ['Ja', 'Nei'],
          },
        ],
      },
      {
        index: 2,
        toggled: false,
        name: 'Annen utdanning',
        questionsAndAnswers: [
          {
            question: 'Hva for annen utdanning har du?',
            answers: [
              'Har startet på høyere utdanning på Høyskole/Universitet',
              'Ingen av alternativene over passer',
              'Har fullført høyere utdanning fra Høyskole eller Universitet ',
            ],
          },
        ],
      },
    ];
    this.buildEducationForm(education);
    this.buildInterestsAndDreamsForm();
    this.buildSendForm();
    this.buildCoursesForm();
    const vipps = this.vippsInfo.infoVipps
    // console.log('vipps called', vipps)
    if(vipps?.firstname || vipps?.surname || vipps?.birthdate){
      this._isUserAuthorizedThroughVipps = true;
    }
    this.buildInfoForm(this.vippsInfo.infoVipps);
  }

  buildEducationForm(data: any[]) {
    const ques = this._fb.array([]);
    data.forEach((section) => {
      let questions = this._fb.array([]);
      if (section.questionsAndAnswers) {
        section.questionsAndAnswers.forEach((set) => {
          const educationAnswerGroup = this._fb.group({
            question: [set.question],
            answers: [set.answers],
            id: [{value: set?.id, disabled: true}],
            answer: ['', [this.ValidateAnswer]],
            textAnswer: ['', [this.ValidateAnswerText]],
          });
          educationAnswerGroup.valueChanges.subscribe((res) => {
            educationAnswerGroup.get('textAnswer').setErrors(null);
          });
          questions.push(educationAnswerGroup);
        });
      }
      let toggledControl = new FormControl(false);
      if (section.index === 0) {
        toggledControl = new FormControl(false, Validators.requiredTrue);
      }
      const sectionGroup = this._fb.group({
        toggled: toggledControl,
        name: [section.name],
        questions: questions,
      });
      ques.push(sectionGroup);
      sectionGroup.get('toggled').valueChanges.subscribe((toggled: boolean) => {
        if (toggled === false) {
          sectionGroup.get('questions')['controls'].forEach((question) => {
            question.get('answer').setValue('');
            question.get('textAnswer').setValue('');
          });
        }
      });
    });
    this._educationForm.next(
      this._fb.group({
        values: ques,
      })
    );
  }

  ValidateAnswer(control: AbstractControl): { [key: string]: any } | null {
    const v: string = control.value;
    if (
      control?.parent?.parent?.parent?.get('toggled').value == true &&
      v.length === 0
    ) {
      return { valueRequired: true };
    }
    return null;
  }

  ValidateAnswerText(control: AbstractControl): { [key: string]: any } | null {
    const v: string = control.value;
    if (
      control?.parent?.parent?.parent?.get('toggled').value == true &&
      control?.parent?.get('answer').value ===
        'Ingen av alternativene over passer' &&
      v.length === 0
    ) {
      return { valueRequired: true };
    }
    return null;
  }

  buildInterestsAndDreamsForm() {
    combineLatest([
      this.apiService.getDreams(),
      this.apiService.getInterests(),
    ]).subscribe(([dreams, interests]) => {
      dreams.value?.push({
        new_name: 'Ingen av alternativene over passer',
      });
      interests.value?.push({
        new_name: 'Ingen av alternativene over passer',
      });
      this._interestsAndDreamsForm.next(
        this._fb.group({
          interests: this.buildFormArray(interests.value, Validators.required),
          dreams: this.buildFormArray(dreams.value, Validators.required),
        })
      );
    });
  }
  buildSendForm() {
    combineLatest([
      this.apiService.getAffiliates(),
      this.apiService.getDescription(),
    ]).subscribe(([affilatesRes, descriptionRes]) => {
      affilatesRes.value?.push({
        new_name: 'Ingen av alternativene over passer',
      });
      this._affilatesForm.next(
        this._fb.group({
          affilates: this.buildFormArray(
            affilatesRes.value,
            this.AffilatesValidator
          ),
          additional_info: descriptionRes?.value[0]?.new_description,
          acceptTerms: [false, [Validators.requiredTrue]],
        })
      );
    });
  }

  AffilatesValidator(control: AbstractControl): { [key: string]: any } | null {
    if (control['controls'].filter((v) => v.value.checked).length === 0) {
      return { valueRequired: true };
    }
    return null;
  }

  buildCoursesForm() {
    this.apiService.getCourses().subscribe((res) => {
      this._coursesForm.next(
        this._fb.group({
          courses: this.buildCourseArray(
            res.value.filter((v) => v.new_showonweb === true)
          ),
          checked: ['', Validators.required],
        })
      );
    });
  }

  buildInfoForm(data: InfoForm) {
    if (data.birthdate?.includes('-')) {
      const bd = data.birthdate?.split('-');
      const year = bd[0];
      bd[0] = bd[2];
      bd[2] = year;
      data.birthdate = bd.join('/');
    }
    if (data.phone_number !== null) {
      const formatedNumber = data.phone_number
        .split('')
        .reduce((all, one, i) => {
          const ch = Math.floor(i / 2);
          all[ch] = [].concat(all[ch] || [], one);
          return all;
        }, [])
        .map((v) => v.join(''))
        .join(' ');
      data.phone_number = formatedNumber;
    }

    this._infoForm.next(
      this._fb.group({
        email: [
          data.email,
          [
            Validators.required,
            Validators.email,
            Validators.pattern('^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$'),
          ],
        ],
        mobile: [
          data.phone_number,
          [
            Validators.required,
            Validators.maxLength(14),
            // Validators.minLength(2),
            // Validators.pattern(/\d{2}\s\d{2}\s\d{2}\s\d{2}\s\d{2}$/),
          ],
        ],
        firstname: [
          data.firstname,
          [Validators.required, Validators.minLength(3)],
        ],
        surname: [data.surname, [Validators.required, Validators.minLength(3)]],
        address: [data.address, [Validators.required, Validators.minLength(1)]],
        postcode: [
          data.postcode,
          [
            Validators.required,
            Validators.minLength(4),
            ,
            Validators.maxLength(4),
            Validators.min(0),
            Validators.max(9999),
          ],
        ],
        city: [data.city, [Validators.required, Validators.minLength(1)]],
        birthday: [
          data.birthdate,
          [
            Validators.required,
            Validators.pattern(/^[0-3]{0,1}[0-9]\/[0-1]{0,1}[0-9]\/\d{4}$/),
            isDateValidator(),
            // Validators.minLength(10),
            Validators.maxLength(10),
          ],
        ],
        parent_phone: [data.parent_phone],
        parent_email: [data.parent_email],
        ssn: [
          null,
          [
            Validators.required,
            ,
            Validators.minLength(5),
            Validators.maxLength(5),
          ],
        ],
        gender: [null, [Validators.required]],
      })
    );
  }

  buildFormArray(dataArray: any[], Validator: any = undefined): FormArray {
    const arr: FormArray = Validator
      ? this._fb.array([], [Validator])
      : this._fb.array([]);
    dataArray?.forEach((v) => {
      let id: string = null;
      if (v.new_affiliateid) {
        id = v.new_affiliateid;
      } else if (v.new_interestsid) {
        id = v.new_interestsid;
      } else if (v.new_dreamid) {
        id = v.new_dreamid;
      }
      arr.push(this.buildArrayGroup(v.new_name, id));
    });
    return arr;
  }

  buildArrayGroup(value: string, id: string = null): FormGroup {
    const fg: FormGroup = this._fb.group({
      question: [value],
      additionalValue: [null],
      id: [id],
      checked: [false],
    });
    return fg;
  }

  buildCourseArray(dataArray: any[]): FormArray {
    const arr: FormArray = this._fb.array([]);
    dataArray.forEach((v) => {
      arr.push(
        this.buildCourseArrayGroup(
          v.new_courseid,
          v.new_courseyear,
          v.new_name,
          v.new_courseimage
        )
      );
    });
    return arr;
  }
  buildCourseArrayGroup(id: string, year: string, name: string, image: string) {
    const fg: FormGroup = this._fb.group({
      id: [id],
      year: [year],
      name: [name.slice(0, name.indexOf(' '))],
      image: [image],
      lenght: [name.slice(name.indexOf(' ') + 1, name.length)],
    });
    return fg;
  }

  getInfoSendData() {
    const obj = JSON.parse(JSON.stringify(this.infoFormV.value))
    obj.mobile = '+' + obj.mobile
    return obj;
  }

  getCourseSendData() {
    return [{ reference_id: this.coursesFormV.get('checked').value }];
  }

  getEducationSendData() {
    const formData: any = this.educationFormV.getRawValue();
    const data = formData.values
      .map((v) => {
        if (v?.name?.includes('grunnskolen')) {
          v.questions = [{ question: 'grunnskolen', answer: 'true' }];
        }
        return v;
      })
      .filter((v) => v.questions?.length !== 0);
    const plainQuestions = [];
    data.forEach((set: any) => {
      set.questions.forEach((question) => {
        if (question.answer === 'Ingen av alternativene over passer') {
          question.answer = question.textAnswer;
        }
        delete question.textAnswer;
        delete question.answers;
        if (question.answer.length > 0) {
          plainQuestions.push(question);
        }
      });

      delete set.toggled;
      delete set.name;
    });
    return plainQuestions;
  }

  getInterestsSendData() {
    const dreams = this.interestFormV
      .get('dreams')
      .value.filter((v: any) => v.checked)
      .map((v: any) => {
        return {
          name: v.additionalValue ? v.additionalValue : v.question,
          reference_id: v.id,
        };
      });
    const interests = this.interestFormV
      .get('interests')
      .value.filter((v: any) => v.checked)
      .map((v: any) => {
        return {
          name: v.additionalValue ? v.additionalValue : v.question,
          reference_id: v.id,
        };
      });
    return { interests, dreams };
  }

  getAffilatesSendData() {
    const formData: any = this.affilatesFormV.value;
    const data = formData.affilates
      .filter((v: any) => v.checked)
      .map((v: any) => {
        return {
          name: v.additionalValue ? v.additionalValue : v.question,
          reference_id: v.id,
        };
      });
    return data;
  }

  clearForms() {
    if (this.infoFormV !== undefined) {
      this.infoFormV.reset();
    }
    if (this.coursesFormV !== undefined) {
      this.coursesFormV.reset();
    }
    if (this.educationFormV !== undefined) {
      this.educationFormV.reset();
    }
    if (this.interestFormV !== undefined) {
      this.interestFormV.reset();
    }
    if (this.affilatesFormV !== undefined) {
      this.affilatesFormV.reset();
    }
  }

  getSendData(): any {
    const form = {
      contact: this.getInfoSendData(),
      course: this.getCourseSendData(),
      affilates: this.getAffilatesSendData(),
      education: this.getEducationSendData(),
      dreams: this.getInterestsSendData().dreams,
      interests: this.getInterestsSendData().interests,
    };
    return form;
  }
}
